import React, {
    useLayoutEffect, useEffect, useRef, useState,
} from "react";
import Image from "@root/components/image";
import Icon from "../Icon/icon";
import ResponsiveWrapper from "../_responsive-wrapper";

const ColorItem = ({
    item, value, onChange, disableHover, isPreviewActive, onPreviewEnter, onTouchPreview,
}) => {
    const hoverImageRef = useRef(null);
    const [translateX, setTranslateX] = useState(0);
    const [windowWidth, setWindowWidth] = useState(0);
    const [supportsHover, setSupportsHover] = useState(true);

    // on window resize, reposition the image
    useEffect(() => {
        if (typeof window === "undefined") return;

        const hoverQuery = window.matchMedia?.("(hover: hover) and (pointer: fine)");
        const updateHoverSupport = () => setSupportsHover(Boolean(hoverQuery?.matches));
        updateHoverSupport();

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);

        if (hoverQuery?.addEventListener) hoverQuery.addEventListener("change", updateHoverSupport);

        return () => {
            window.removeEventListener("resize", handleResize);
            if (hoverQuery?.removeEventListener) hoverQuery.removeEventListener("change", updateHoverSupport);
        };
    }, []);

    useLayoutEffect(() => {
        if (hoverImageRef.current) {
            const imageRect = hoverImageRef.current.getBoundingClientRect();
            setTranslateX(Math.max(imageRect.right - window.innerWidth + 10 + translateX, 0) + 15);
        }
    }, [value, windowWidth]);

    const backgroundStyle = {
        "--color": item.color,
        ...(item.passiveColor && { "--gradient": `linear-gradient(-90deg, ${item.color} 50%, ${item.passiveColor} 50%)` }),
    };

    const canPreviewImage = !disableHover && item.image;

    return (
        <div
            className={`color-swatch__option ${item.passiveColor ? "color-swatch__option--passive-color" : ""}`}
            style={backgroundStyle}
        >

            <label
                className="color-swatch__color"
                data-disable-hover={(disableHover || !supportsHover) ? "true" : undefined}
                data-preview-active={isPreviewActive ? "true" : undefined}
                onMouseEnter={canPreviewImage && supportsHover ? onPreviewEnter : undefined}
                onTouchStart={canPreviewImage && !supportsHover ? onTouchPreview : undefined}
                onTouchEnd={() => onChange(item.name)}
            >
                <input
                    type="radio"
                    className="color-swatch__input"
                    checked={value === item.name}
                    onChange={() => onChange(item.name)}
                />
                {value === item.name ? (
                    <Icon icon="check" className="color-swatch__icon"/>
                ) : null}
                {canPreviewImage && (
                    <div
                        className="color-swatch__image"
                        ref={hoverImageRef}
                        style={{"--translateX": `-${translateX}px`}}
                    >
                        <Image image={item.image} alt={item.name}/>
                    </div>
                )}
            </label>
        </div>
    );
};

const ColorSwatch = ({ baseLabel = "Color:", colors, value, onChange, noLabel, disableHover, size }) => {
    const [activePreview, setActivePreview] = useState(null);
    const touchPreviewTimeoutRef = useRef(null);

    useEffect(() => () => {
        if (touchPreviewTimeoutRef.current) {
            clearTimeout(touchPreviewTimeoutRef.current);
            touchPreviewTimeoutRef.current = null;
        }
    }, []);

    const clearPreview = () => {
        setActivePreview(null);
        if (touchPreviewTimeoutRef.current) {
            clearTimeout(touchPreviewTimeoutRef.current);
            touchPreviewTimeoutRef.current = null;
        }
    };

    const showPreview = (name) => {
        if (touchPreviewTimeoutRef.current) {
            clearTimeout(touchPreviewTimeoutRef.current);
            touchPreviewTimeoutRef.current = null;
        }
        setActivePreview(name);
    };

    const showTouchPreview = (name) => {
        showPreview(name);
        touchPreviewTimeoutRef.current = setTimeout(() => {
            setActivePreview(null);
            touchPreviewTimeoutRef.current = null;
        }, 1500);
    };

    return (
        <ResponsiveWrapper className={`color-swatch ${size ? ` color-swatch--${size}` : ""}`}>
            {noLabel !== true ? (
                <label className="color-swatch__label" onClick={() => onChange(value)}>
                    <span>{baseLabel}</span>
                    {value}
                </label>
            ) : null}
            <div
                className="color-swatch__options"
                onMouseLeave={!disableHover ? clearPreview : undefined}
            >
                {colors?.map((item) => (
                    <ColorItem
                        key={item.name}
                        item={item}
                        value={value}
                        onChange={(value) => onChange(value)}
                        disableHover={disableHover}
                        isPreviewActive={activePreview === item.name}
                        onPreviewEnter={() => showPreview(item.name)}
                        onTouchPreview={() => showTouchPreview(item.name)}
                    />
                ))}
            </div>
        </ResponsiveWrapper>
    );
};

export default ColorSwatch;
